/**
 * Vol des pièces (E§1.2).
 *
 * « Les pièces ne s'incrémentent pas : elles volent physiquement depuis le bâtiment
 * producteur jusqu'au compteur, en arc de Bézier, avec rotation propre et arrivée
 * décalée. »
 *
 * Trois décisions structurantes.
 *
 * **Les pièces volent en espace ÉCRAN, pas en espace monde.** Elles quittent un bâtiment
 * — donc le monde — pour rejoindre un compteur HTML posé au-dessus du canvas. Les faire
 * vivre dans le conteneur `world` les ferait dériver au moindre déplacement de caméra
 * pendant leur vol, et le point d'arrivée bougerait sous elles. Le point de départ est
 * donc converti une fois, à l'émission, et la trajectoire se joue dans un calque fixe.
 *
 * **Une texture unique, dessinée une fois.** Le pack 3D ne contient pas de pièce utilisable telle quelle.
 * Plutôt que d'importer un asset de plus, on rend un `Graphics` en texture au démarrage :
 * toutes les pièces la partagent, donc une seule passe de rendu pour la volée entière.
 *
 * **Le plafond est une règle, pas une intention.** E§3 est catégorique : le juice ne doit
 * jamais coûter des fps. Au-delà de `maxFlightsPerSecond`, on cesse d'émettre une pièce
 * par gain et on agrège dans une pièce « grosse ». Au-delà de `maxConcurrentFlights`, la
 * réserve rend `null` et le gain est simplement crédité sans animation — un joueur ne
 * remarque pas une pièce manquante dans une volée, il remarque une saccade.
 */

import { type Container, Graphics, Sprite, type Texture } from 'pixi.js';
import type { Renderer } from 'pixi.js';

import { JUICE } from '@/config/juice';
import { quadraticBezier } from '@/core/tween';
import type { TweenRunner } from '@/core/tween';
import { Pool } from './pool';

/** Point en pixels CSS, repère du canvas. */
export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface CoinFlightOptions {
  /** Calque parent, ajouté à la scène AU-DESSUS du monde. */
  readonly layer: Container;
  readonly tweens: TweenRunner;
  /** Où atterrissent les pièces — relu à chaque volée, le HUD pouvant se déplacer. */
  readonly target: () => ScreenPoint;
  /** Appelé à chaque arrivée : c'est là que le compteur pulse et que le son part. */
  readonly onArrival: (value: number) => void;
  /**
   * Appelé au DÉPART d'une pièce, avec la valeur qu'elle transporte réellement —
   * agrégat compris. C'est l'accroche du nombre qui gicle (B.1) : le même événement,
   * le même plafond de débit, donc jamais un nombre sans pièce ni l'inverse.
   */
  readonly onLaunch?: (from: ScreenPoint, value: number, now: number) => void;
}

const COIN_RADIUS = 11;

/**
 * Dessine la pièce une seule fois et la rend en texture.
 *
 * Un `Graphics` par pièce serait rejoué à chaque frame par le processeur graphique ;
 * une texture partagée se contente d'un quad, et les quarante pièces d'une volée
 * tiennent dans un seul lot de rendu.
 */
function makeCoinTexture(renderer: Renderer): Texture {
  const g = new Graphics()
    .circle(COIN_RADIUS, COIN_RADIUS, COIN_RADIUS)
    .fill({ color: 0xf7b23b })
    .circle(COIN_RADIUS, COIN_RADIUS, COIN_RADIUS)
    .stroke({ color: 0xb9761a, width: 2 })
    .circle(COIN_RADIUS, COIN_RADIUS, COIN_RADIUS * 0.45)
    .stroke({ color: 0xb9761a, width: 1.6 });

  const texture = renderer.generateTexture(g);
  g.destroy();
  return texture;
}

/**
 * Nombre de pièces d'une pluie de retour (B.6), sur l'échelle LOG du gain.
 *
 * Pure et exportée : c'est la partie testable sans rendu — même raison que
 * `gainFontPx`. Le plancher garantit qu'un petit retour pleut quand même un peu, le
 * plafond que trois jours d'absence ne coûtent pas une frame.
 */
export function rainCount(total: number): number {
  const { minCoins, maxCoins, coinsPerMagnitude } = JUICE.returnRain;
  const magnitude = Math.log10(Math.max(1, total));
  return Math.max(minCoins, Math.min(maxCoins, Math.round(magnitude * coinsPerMagnitude)));
}

export class CoinFlight {
  private readonly pool: Pool<Sprite>;
  private readonly texture: Texture;

  /** Horodatages des émissions de la dernière seconde glissante. */
  private recent: number[] = [];

  /** Gains mis de côté pendant la saturation, restitués par une pièce « grosse ». */
  private pending = 0;

  constructor(
    renderer: Renderer,
    private readonly options: CoinFlightOptions,
  ) {
    this.texture = makeCoinTexture(renderer);

    this.pool = new Pool<Sprite>({
      create: () => {
        const sprite = new Sprite(this.texture);
        sprite.anchor.set(0.5);
        sprite.visible = false;
        this.options.layer.addChild(sprite);
        return sprite;
      },
      reset: (sprite) => {
        sprite.visible = false;
        sprite.rotation = 0;
        sprite.alpha = 1;
        sprite.scale.set(1);
      },
      initialSize: 8,
      maxSize: JUICE.coins.maxConcurrentFlights,
    });
  }

  /**
   * Lance une pièce depuis un point écran.
   *
   * Rend `true` si la valeur a été confiée à une animation, `false` si elle a été
   * absorbée par le plafond — dans ce cas l'appelant crédite quand même le joueur :
   * **le juice n'a jamais le droit de faire perdre des pièces**.
   */
  /**
   * `quiet` (B.6) : la pièce vole sans déclencher le nombre qui gicle. La pluie du
   * retour fait pleuvoir une VINGTAINE de pièces pour un seul gain déjà annoncé par
   * l'écran de retour — vingt étiquettes « +0 » seraient du bruit, pas du juice.
   */
  emit(from: ScreenPoint, value: number, now: number, quiet = false): boolean {
    this.recent = this.recent.filter((at) => now - at < 1000);

    if (this.recent.length >= JUICE.coins.maxFlightsPerSecond) {
      // Saturation : on met de côté au lieu d'émettre. La prochaine pièce autorisée
      // partira « grosse » et portera tout ce qui a été accumulé.
      this.pending += value;
      return false;
    }

    const aggregated = this.pending > 0;
    const carried = value + this.pending;
    this.pending = 0;

    const sprite = this.pool.acquire();
    if (sprite === null) {
      // Plafond dur atteint : pas d'animation, mais la valeur revient à l'appelant.
      this.pending += carried;
      return false;
    }

    this.recent.push(now);
    if (!quiet) this.options.onLaunch?.(from, carried, now);

    const to = this.options.target();
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy);

    // L'arc s'ouvre perpendiculairement au trajet : une pièce qui suit la droite
    // exacte entre deux points paraît aspirée, pas lancée.
    const arc = distance * JUICE.coins.arcHeightRatio;
    const controlX = (from.x + to.x) / 2 - (dy / (distance || 1)) * arc;
    const controlY = (from.y + to.y) / 2 + (dx / (distance || 1)) * arc - arc * 0.5;

    sprite.visible = true;
    sprite.position.set(from.x, from.y);
    sprite.scale.set(aggregated ? 1.6 : 1);
    sprite.zIndex = 10;

    const spin = (Math.random() < 0.5 ? -1 : 1) * Math.PI * 2;

    this.options.tweens.add(
      {
        from: 0,
        to: 1,
        durationMs: JUICE.coins.flightMs,
        easing: 'inOut',
        // Le décalage donne à une volée son allure de grappe plutôt que de peloton.
        delayMs: (this.recent.length % 4) * JUICE.coins.staggerMs,
        onUpdate: (t) => {
          const p = quadraticBezier(from.x, from.y, controlX, controlY, to.x, to.y, t);
          sprite.position.set(p.x, p.y);
          sprite.rotation = spin * t;
          // Elle ne disparaît qu'au tout dernier moment : s'effacer plus tôt ferait
          // arriver le compteur « tout seul », sans lien visible avec la pièce.
          sprite.alpha = t > 0.88 ? 1 - (t - 0.88) / 0.12 : 1;
        },
        onComplete: () => {
          this.pool.release(sprite);
          this.options.onArrival(carried);
        },
      },
      now,
    );

    return true;
  }

  /** Pièces actuellement en vol — lu par la sonde de debug et les tests. */
  get inFlight(): number {
    return this.pool.inUse;
  }

  destroy(): void {
    this.texture.destroy(true);
  }
}
